import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useModalManager from "@/hooks/use-modal-manager";
import {
  Plus,
  Search,
  SortAsc,
  SortDesc,
  Bell,
  MessageCircle,
  Eye,
  Calendar,
  AlertTriangle,
  Info,
  Megaphone,
  GraduationCap,
  PartyPopper,
  FileText,
  MoreVertical,
  Edit2,
  Trash2,
  Filter,
  X,
  ChevronDown,
  Pin,
  Clock,
  Users,
  TrendingUp,
  Star,
  Bookmark,
  Share2,
  CheckCircle,
  Zap,
  Briefcase,
  Home,
  Settings,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import WorkingAnnouncementModal from "@/components/modals/WorkingAnnouncementModal";
import AnnouncementDetailModal from "@/components/modals/AnnouncementDetailModal";
import announcementService from "@/services/announcement.service";
import { formatDate, formatTimeAgo } from "@/lib/utils";

// Enhanced type icons with better visual hierarchy
const typeIcons = {
  info: Info,
  urgente: AlertTriangle,
  evenement: Calendar,
  procedure: FileText,
  celebration: PartyPopper,
  formation: GraduationCap,
  autre: Megaphone,
};

// Professional color scheme inspired by enterprise platforms
const typeColors = {
  info: {
    bg: "bg-gradient-to-br from-blue-50 to-blue-100/50",
    text: "text-blue-700",
    border: "border-blue-200/50",
    icon: "text-blue-600",
    badge: "bg-blue-100 text-blue-700 border-blue-200",
    hover: "hover:from-blue-100 hover:to-blue-200/50",
  },
  urgente: {
    bg: "bg-gradient-to-br from-red-50 to-red-100/50",
    text: "text-red-700",
    border: "border-red-200/50",
    icon: "text-red-600",
    badge: "bg-red-100 text-red-700 border-red-200",
    hover: "hover:from-red-100 hover:to-red-200/50",
  },
  evenement: {
    bg: "bg-gradient-to-br from-purple-50 to-purple-100/50",
    text: "text-purple-700",
    border: "border-purple-200/50",
    icon: "text-purple-600",
    badge: "bg-purple-100 text-purple-700 border-purple-200",
    hover: "hover:from-purple-100 hover:to-purple-200/50",
  },
  procedure: {
    bg: "bg-gradient-to-br from-slate-50 to-slate-100/50",
    text: "text-slate-700",
    border: "border-slate-200/50",
    icon: "text-slate-600",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    hover: "hover:from-slate-100 hover:to-slate-200/50",
  },
  celebration: {
    bg: "bg-gradient-to-br from-pink-50 to-pink-100/50",
    text: "text-pink-700",
    border: "border-pink-200/50",
    icon: "text-pink-600",
    badge: "bg-pink-100 text-pink-700 border-pink-200",
    hover: "hover:from-pink-100 hover:to-pink-200/50",
  },
  formation: {
    bg: "bg-gradient-to-br from-emerald-50 to-emerald-100/50",
    text: "text-emerald-700",
    border: "border-emerald-200/50",
    icon: "text-emerald-600",
    badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
    hover: "hover:from-emerald-100 hover:to-emerald-200/50",
  },
  autre: {
    bg: "bg-gradient-to-br from-orange-50 to-orange-100/50",
    text: "text-orange-700",
    border: "border-orange-200/50",
    icon: "text-orange-600",
    badge: "bg-orange-100 text-orange-700 border-orange-200",
    hover: "hover:from-orange-100 hover:to-orange-200/50",
  },
};

// Professional priority styling
const priorityColors = {
  normale: {
    bg: "bg-slate-100/80 text-slate-700 border-slate-200/50",
    icon: "text-slate-500",
  },
  importante: {
    bg: "bg-amber-100/80 text-amber-700 border-amber-200/50",
    icon: "text-amber-500",
  },
  urgente: {
    bg: "bg-red-100/80 text-red-700 border-red-200/50",
    icon: "text-red-500",
  },
};

const AnnouncementsPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Modal management
  const {
    openModal,
    closeModal,
    closeAllModals,
    switchModal,
    getModalState,
    initModal,
  } = useModalManager();

  // Local state
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // grid, list, compact
  const [activeTab, setActiveTab] = useState("all");

  // Filters and search
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [typeFilter, setTypeFilter] = useState(
    searchParams.get("type") || "all"
  );
  const [sortBy, setSortBy] = useState(
    searchParams.get("sortBy") || "date_publication"
  );
  const [sortOrder, setSortOrder] = useState(
    searchParams.get("sortOrder") || "desc"
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get("page")) || 1
  );

  // Permissions
  const canCreateAnnouncement = ["admin", "rh", "manager"].includes(user?.role);

  // Enhanced stats computation
  const stats = {
    total: announcements.length,
    unread: announcements.filter((a) => !a.isRead).length,
    urgent: announcements.filter((a) => a.priorite === "urgente").length,
    thisWeek: announcements.filter((a) => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(a.date_publication) > weekAgo;
    }).length,
    byType: announcements.reduce((acc, a) => {
      acc[a.type_annonce] = (acc[a.type_annonce] || 0) + 1;
      return acc;
    }, {}),
  };

  // Fetch announcements
  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await announcementService.getAnnouncements({
        page: currentPage,
        limit: 12,
        search: searchTerm,
        type: typeFilter === "all" ? undefined : typeFilter,
        sortBy,
        sortOrder,
        actif: true,
      });

      setAnnouncements(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Erreur lors du chargement des annonces:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les annonces",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Load data effect
  useEffect(() => {
    fetchAnnouncements();
  }, [currentPage, searchTerm, typeFilter, sortBy, sortOrder]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (typeFilter !== "all") params.set("type", typeFilter);
    if (sortBy !== "date_publication") params.set("sortBy", sortBy);
    if (sortOrder !== "desc") params.set("sortOrder", sortOrder);
    if (currentPage !== 1) params.set("page", currentPage.toString());

    setSearchParams(params);
  }, [searchTerm, typeFilter, sortBy, sortOrder, currentPage, setSearchParams]);

  // Initialize modals
  useEffect(() => {
    initModal("create");
    initModal("detail");
    initModal("edit");
    initModal("delete");
  }, [initModal]);

  // Event handlers
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleAnnouncementClick = async (announcement) => {
    setSelectedAnnouncement(announcement);
    openModal("detail", announcement);
  };

  const handleEditAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    switchModal("detail", "edit", announcement);
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette annonce ?")) {
      return;
    }

    try {
      await announcementService.deleteAnnouncement(id);
      toast({
        title: "Succès",
        description: "Annonce supprimée avec succès",
      });
      fetchAnnouncements();
      closeAllModals();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'annonce",
        variant: "destructive",
      });
    }
  };

  const handleCreateSuccess = () => {
    closeModal("create");
    setSelectedAnnouncement(null);
    fetchAnnouncements();
    toast({
      title: "Succès",
      description: "Annonce créée avec succès",
    });
  };

  const handleUpdateSuccess = () => {
    closeModal("edit");
    setSelectedAnnouncement(null);
    fetchAnnouncements();
    toast({
      title: "Succès",
      description: "Annonce mise à jour avec succès",
    });
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setTypeFilter("all");
    setSortBy("date_publication");
    setSortOrder("desc");
    setCurrentPage(1);
  };

  // Professional Stats Card Component
  const StatsCard = ({ icon: Icon, label, value, color = "blue", trend }) => (
    <motion.div whileHover={{ scale: 1.02 }} className="group cursor-pointer">
      <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-gradient-to-br from-white to-gray-50/50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div
                className={`p-3 rounded-xl bg-gradient-to-br ${typeColors[color]?.bg || "bg-blue-50"
                  } group-hover:scale-110 transition-transform duration-300`}
              >
                <Icon
                  className={`h-6 w-6 ${typeColors[color]?.icon || "text-blue-600"
                    }`}
                />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-sm text-gray-600 font-medium">{label}</p>
              </div>
            </div>
            {trend && (
              <div
                className={`text-sm font-medium ${trend > 0 ? "text-green-600" : "text-red-600"
                  }`}
              >
                {trend > 0 ? "+" : ""}
                {trend}%
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  // Professional Announcement Card Component
  const AnnouncementCard = ({ announcement, index }) => {
    const TypeIcon = typeIcons[announcement.type_annonce] || Megaphone;
    const colors = typeColors[announcement.type_annonce];
    const canEdit =
      canCreateAnnouncement &&
      (user.role === "admin" ||
        user.role === "rh" ||
        announcement.publieur?.id === user.id);

    const isPinned = announcement.priorite === "urgente";
    const isNew = !announcement.isRead;

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileHover={{ y: -4 }}
        className="group relative"
      >
        <Card
          className={`h-full cursor-pointer transition-all duration-300 border-0 shadow-sm hover:shadow-lg overflow-hidden ${colors.border} bg-white`}
          onClick={() => handleAnnouncementClick(announcement)}
        >
          {/* Priority indicator */}
          {isPinned && (
            <div className="absolute top-4 right-16 z-10">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="bg-red-500 text-white p-2 rounded-full shadow-lg"
              >
                <Pin className="h-4 w-4" />
              </motion.div>
            </div>
          )}

          {/* New indicator */}
          {isNew && (
            <div className="absolute top-1 left-2 z-20">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="bg-green-500 text-white px-2 py-0.5 rounded-full text-xs font-medium shadow-lg"
              >
                Nouveau
              </motion.div>
            </div>
          )}

          <CardHeader className="pb-3 pt-8 relative z-10">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start space-x-4 flex-1 min-w-0">
                {/* Type icon with enhanced styling */}
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                  className={`p-2.5 rounded-lg shadow-sm ${colors.bg} ${colors.hover} transition-all duration-300 flex-shrink-0`}
                >
                  <TypeIcon className={`h-5 w-5 ${colors.icon}`} />
                </motion.div>

                <div className="flex-1 min-w-0">
                  {/* Badges */}
                  <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                    <Badge
                      variant="secondary"
                      className={`text-xs px-2 py-0.5 font-medium ${colors.badge}`}
                    >
                      {announcement.type_annonce.charAt(0).toUpperCase() +
                        announcement.type_annonce.slice(1)}
                    </Badge>
                    <Badge
                      variant="secondary"
                      className={`text-xs px-2 py-0.5 font-medium ${priorityColors[announcement.priorite].bg
                        }`}
                    >
                      {announcement.priorite.charAt(0).toUpperCase() +
                        announcement.priorite.slice(1)}
                    </Badge>
                  </div>

                  {/* Title */}
                  <CardTitle className="text-base font-semibold leading-tight line-clamp-2 group-hover:text-blue-600 transition-colors cursor-pointer mb-1.5">
                    {announcement.titre}
                  </CardTitle>

                  {/* Description */}
                  <CardDescription className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                    {announcement.contenu}
                  </CardDescription>
                </div>
              </div>

              {/* Action menu */}
              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="bg-white shadow-xl border border-gray-200"
                  >
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditAnnouncement(announcement);
                      }}
                    >
                      <Edit2 className="mr-2 h-4 w-4" />
                      Modifier
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Bookmark className="mr-2 h-4 w-4" />
                      Marquer
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Share2 className="mr-2 h-4 w-4" />
                      Partager
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAnnouncement(announcement.id);
                      }}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </CardHeader>

          <CardContent className="pt-0 relative z-10">
            <Separator className="mb-3" />

            {/* Author and metadata */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center space-x-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center space-x-2">
                      <Avatar className="h-6 w-6 border border-gray-200">
                        <AvatarImage
                          src={announcement.publieur?.photo_profil}
                        />
                        <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-600 text-white text-xs font-medium">
                          {announcement.publieur?.prenom?.[0]}
                          {announcement.publieur?.nom?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-gray-700 truncate text-xs">
                        {announcement.publieur?.prenom}{" "}
                        {announcement.publieur?.nom}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Publié par</TooltipContent>
                </Tooltip>

                <Separator orientation="vertical" className="h-4" />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span className="font-medium text-xs">
                        {formatTimeAgo(announcement.date_publication)}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Date de publication</TooltipContent>
                </Tooltip>
              </div>

              {/* Engagement metrics */}
              <div className="flex items-center space-x-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="flex items-center space-x-1 cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAnnouncementClick(announcement);
                      }}
                    >
                      <Eye className="h-3 w-3" />
                      <span className="font-medium text-xs">
                        {announcement._count?.lectures || 0}
                      </span>
                    </motion.div>
                  </TooltipTrigger>
                  <TooltipContent>Vues</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="flex items-center space-x-1"
                    >
                      <MessageCircle className="h-3 w-3" />
                      <span className="font-medium text-xs">
                        {announcement._count?.commentaires || 0}
                      </span>
                    </motion.div>
                  </TooltipTrigger>
                  <TooltipContent>Commentaires</TooltipContent>
                </Tooltip>
              </div>
            </div>

            {/* Expiration notice */}
            {announcement.date_expiration && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 flex items-center space-x-2 text-xs bg-amber-50 border border-amber-200 rounded-md p-2"
              >
                <Calendar className="h-3 w-3 text-amber-600" />
                <span className="text-amber-700 font-medium">
                  Expire le {formatDate(announcement.date_expiration)}
                </span>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // Professional Loading Skeleton
  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i} className="h-64 border-0 shadow-sm bg-white">
          <CardHeader>
            <div className="flex space-x-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse" />
              <div className="space-y-2 flex-1">
                <div className="h-4 w-3/4 bg-gradient-to-r from-gray-200 to-gray-300 animate-pulse rounded" />
                <div className="h-3 w-full bg-gradient-to-r from-gray-200 to-gray-300 animate-pulse rounded" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="h-6 w-full bg-gradient-to-r from-gray-200 to-gray-300 animate-pulse rounded" />
              <div className="h-4 w-2/3 bg-gradient-to-r from-gray-200 to-gray-300 animate-pulse rounded" />
              <div className="h-4 w-4/5 bg-gradient-to-r from-gray-200 to-gray-300 animate-pulse rounded" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4 py-8 space-y-8">
          {/* Professional Header */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6"
          >
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
                  <Bell className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Annonces</h1>
                  <p className="text-gray-600 font-medium">
                    Restez informé des actualités et événements de l'entreprise
                  </p>
                </div>
              </div>
            </div>

            {canCreateAnnouncement && (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  onClick={() => openModal("create")}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 px-6 py-3 rounded-xl font-medium"
                  size="lg"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  Nouvelle annonce
                </Button>
              </motion.div>
            )}
          </motion.div>

          {/* Enhanced Stats Dashboard */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            <StatsCard
              icon={Bell}
              label="Total Annonces"
              value={stats.total}
              color="info"
            />
            <StatsCard
              icon={TrendingUp}
              label="Non lues"
              value={stats.unread}
              color="formation"
            />
            <StatsCard
              icon={AlertTriangle}
              label="Urgentes"
              value={stats.urgent}
              color="urgente"
            />
            <StatsCard
              icon={Calendar}
              label="Cette semaine"
              value={stats.thisWeek}
              color="evenement"
            />
          </motion.div>

          {/* Professional Search and Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-0 shadow-sm bg-white">
              <CardContent className="p-6 space-y-6">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    placeholder="Rechercher dans les annonces..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-12 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
                  />
                </div>

                {/* Filters and Controls */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="w-[180px] border-gray-200 rounded-lg">
                        <SelectValue placeholder="Tous les types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les types</SelectItem>
                        <SelectItem value="info">Information</SelectItem>
                        <SelectItem value="urgente">Urgente</SelectItem>
                        <SelectItem value="evenement">Événement</SelectItem>
                        <SelectItem value="procedure">Procédure</SelectItem>
                        <SelectItem value="celebration">Célébration</SelectItem>
                        <SelectItem value="formation">Formation</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      variant="outline"
                      onClick={() => toggleSort("date_publication")}
                      className="border-gray-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg"
                    >
                      Date{" "}
                      {sortBy === "date_publication" &&
                        (sortOrder === "asc" ? (
                          <SortAsc className="ml-2 h-4 w-4" />
                        ) : (
                          <SortDesc className="ml-2 h-4 w-4" />
                        ))}
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => toggleSort("priorite")}
                      className="border-gray-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg"
                    >
                      Priorité{" "}
                      {sortBy === "priorite" &&
                        (sortOrder === "asc" ? (
                          <SortAsc className="ml-2 h-4 w-4" />
                        ) : (
                          <SortDesc className="ml-2 h-4 w-4" />
                        ))}
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    {(searchTerm || typeFilter !== "all") && (
                      <Button
                        variant="ghost"
                        onClick={clearFilters}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="mr-2 h-4 w-4" />
                        Effacer filtres
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Announcements Grid */}
          <AnimatePresence>
            {loading ? (
              <LoadingSkeleton />
            ) : announcements.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16"
              >
                <div className="p-8 rounded-2xl bg-white shadow-sm border border-gray-100">
                  <Bell className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Aucune annonce trouvée
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Essayez d'ajuster vos filtres ou revenez plus tard.
                  </p>
                  {canCreateAnnouncement && (
                    <Button
                      onClick={() => openModal("create")}
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Créer la première annonce
                    </Button>
                  )}
                </div>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {announcements.map((announcement, index) => (
                  <AnnouncementCard
                    key={announcement.id}
                    announcement={announcement}
                    index={index}
                  />
                ))}
              </div>
            )}
          </AnimatePresence>

          {/* Professional Pagination */}
          {pagination.totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-between items-center mt-8 p-6 bg-white rounded-xl shadow-sm border border-gray-100"
            >
              <p className="text-sm text-gray-600 font-medium">
                Page {currentPage} sur {pagination.totalPages} •{" "}
                {pagination.totalItems} annonces
              </p>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  disabled={!pagination.hasPrevPage}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="border-gray-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg"
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  disabled={!pagination.hasNextPage}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="border-gray-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg"
                >
                  Suivant
                </Button>
              </div>
            </motion.div>
          )}

          {/* Modals */}
          <WorkingAnnouncementModal
            isOpen={getModalState("create").isOpen}
            onClose={() => {
              closeModal("create");
              setSelectedAnnouncement(null);
            }}
            onSuccess={handleCreateSuccess}
            mode="create"
          />
          <WorkingAnnouncementModal
            isOpen={getModalState("edit").isOpen}
            onClose={() => {
              closeModal("edit");
              setSelectedAnnouncement(null);
            }}
            onSuccess={handleUpdateSuccess}
            mode="edit"
            announcement={selectedAnnouncement}
          />
          <AnnouncementDetailModal
            isOpen={getModalState("detail").isOpen}
            onClose={() => {
              closeModal("detail");
              setSelectedAnnouncement(null);
            }}
            announcement={selectedAnnouncement}
            onEdit={handleEditAnnouncement}
            onDelete={handleDeleteAnnouncement}
            canEdit={canCreateAnnouncement}
          />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default AnnouncementsPage;
